export const OPERATING_STYLES = [
  {
    id: "passive",
    title: "Passive",
    description: "Strong returns with minimal day-to-day involvement",
    examples: "Long leases, supported living, FRI structures",
  },
  {
    id: "active",
    title: "Active",
    description: "Happy sourcing, improving and managing assets without it becoming a second job",
    examples: "HMOs, BRRR, smaller refurbs",
  },
  {
    id: "operational",
    title: "Operational",
    description: "Comfortable running a business on top of property",
    examples: "Serviced accommodation, co-work, hospitality-style models",
  },
  {
    id: "heavy-execution",
    title: "Heavy Execution",
    description: "Happy with complexity, projects, planning and value creation",
    examples: "Conversions, larger refurbs, commercial repositioning",
  },
]

const LEGACY_OPERATING_STYLES = {
  "light-active": "active",
  "high-operational": "operational",
}

export const INVESTMENT_STRATEGIES = [
  {
    id: "brrr",
    title: "Buy, Refurbish, Refinance (BRRR)",
    shortTitle: "BRRR",
    subtitle: "Recycle capital through forced appreciation and long-term rental",
    details: ["Add value through refurbishment", "Refinance to extract capital", "Build portfolio over time"],
    stats: {
      cashflow: "Low",
      equity: "High",
      complexity: "Medium",
      builderHeavy: "Yes",
      speed: "Slow",
      bestFitStyle: "active",
    },
  },
  {
    id: "hmo",
    title: "HMOs",
    shortTitle: "HMOs",
    subtitle: "Maximise rental income through multi-tenant properties",
    details: ["High income per property", "Strong demand in key areas", "Scalable room-by-room model"],
    stats: {
      cashflow: "High",
      equity: "Medium",
      complexity: "Medium",
      builderHeavy: "Yes",
      speed: "Medium",
      bestFitStyle: "active",
    },
  },
  {
    id: "pure-commercial",
    title: "Pure Commercial",
    shortTitle: "Pure Commercial",
    subtitle: "Generate stable income through commercial leases and tenants",
    details: ["Long-term lease agreements", "Lower day-to-day involvement", "Predictable income streams"],
    stats: {
      cashflow: "Low",
      equity: "Low",
      complexity: "Low",
      builderHeavy: "No",
      speed: "Slow",
      bestFitStyle: "passive",
    },
  },
  {
    id: "urban-goldmine",
    title: "Urban Goldmine (Mixed-Use / Repositioning)",
    shortTitle: "Urban Goldmine",
    subtitle: "Unlock hidden value through creative repositioning and structuring",
    details: ["Multiple value-add angles", "Less competition in the market", "Balanced cashflow and upside"],
    stats: {
      cashflow: "Medium",
      equity: "Medium",
      complexity: "Medium",
      builderHeavy: "No",
      speed: "Medium",
      bestFitStyle: "active",
    },
  },
  {
    id: "sa",
    title: "Serviced Accommodation",
    shortTitle: "Serviced Accommodation",
    subtitle: "Short-term rentals generating higher income through nightly stays",
    details: ["Higher income potential", "Flexible pricing model", "Suitable for high-demand locations"],
    stats: {
      cashflow: "High",
      equity: "Medium",
      complexity: "High",
      builderHeavy: "No",
      speed: "Fast",
      bestFitStyle: "operational",
    },
  },
  {
    id: "cowork-serviced-office",
    title: "Co-work / Serviced Office",
    shortTitle: "Co-work / Serviced Office",
    subtitle: "Operational workspace income through serviced office demand",
    details: ["Operational commercial income", "Flexible tenant mix", "Useful in strong business locations"],
    stats: {
      cashflow: "High",
      equity: "Medium",
      complexity: "Medium",
      builderHeavy: "No",
      speed: "Medium",
      bestFitStyle: "operational",
    },
  },
  {
    id: "commercial-conversion",
    title: "Commercial Conversion",
    shortTitle: "Commercial Conversion",
    subtitle: "Create value by converting commercial property to residential use",
    details: ["Significant value uplift potential", "Planning and use-class advantage", "Undervalued asset opportunities"],
    stats: {
      cashflow: "Low",
      equity: "High",
      complexity: "High",
      builderHeavy: "Yes",
      speed: "Slow",
      bestFitStyle: "heavy-execution",
    },
  },
  {
    id: "flips",
    title: "Flips (Buy, Refurbish, Sell)",
    shortTitle: "Flips",
    subtitle: "Generate short-term profit by adding value and selling",
    details: ["Capital recycling", "No long-term tenant management", "Profit realised on sale"],
    stats: {
      cashflow: "Low",
      equity: "High",
      complexity: "High",
      builderHeavy: "Yes",
      speed: "Fast",
      bestFitStyle: "heavy-execution",
    },
  },
]

const STRATEGY_BY_ID = new Map(INVESTMENT_STRATEGIES.map((strategy) => [strategy.id, strategy]))

const STYLE_ADJACENCY = {
  passive: ["active"],
  active: ["passive", "operational", "heavy-execution"],
  operational: ["active", "heavy-execution"],
  "heavy-execution": ["active", "operational"],
}

const COMPLEXITY_SCORE = { Low: 1, Medium: 2, High: 3 }
const CASHFLOW_SCORE = { Low: 1, Medium: 2, High: 3 }
const SPEED_SCORE = { Slow: 1, Medium: 2, Fast: 3 }

export function normalizeOperatingPreference(value) {
  return LEGACY_OPERATING_STYLES[value] || value || ""
}

export function findStrategy(strategyId) {
  return STRATEGY_BY_ID.get(strategyId) || null
}

export function recommendStrategies({
  operatingPreference,
  stepOneValues = {},
  primaryStrategy,
  secondaryStrategy,
}) {
  const normalizedPreference = normalizeOperatingPreference(operatingPreference)
  if (!OPERATING_STYLES.some((style) => style.id === normalizedPreference)) return null

  const profile = stepOneProfile(stepOneValues)
  const rankedStrategies = rankStrategies({ operatingPreference: normalizedPreference, profile })
  const primaryId = validStrategyId(primaryStrategy) || rankedStrategies.at(0)?.id || null
  const secondaryId = nextSecondaryStrategy({
    requestedSecondary: secondaryStrategy,
    primaryStrategy: primaryId,
    rankedStrategies,
  })
  const stretchId = stretchStrategy({
    primaryStrategy: primaryId,
    secondaryStrategy: secondaryId,
    rankedStrategies,
    profile,
  })
  const coachingTensions = coachingTensionsFor({ operatingPreference: normalizedPreference, profile })

  return {
    systemInsight: systemInsightFor({ operatingPreference: normalizedPreference, profile }),
    coachingTensions,
    operatingPreference: normalizedPreference,
    primaryStrategy: primaryId,
    secondaryStrategy: secondaryId,
    stretchStrategy: stretchId,
    primary: findStrategy(primaryId),
    secondary: findStrategy(secondaryId),
    stretch: findStrategy(stretchId),
    recommendations: recommendationCards({ primaryId, secondaryId, stretchId, operatingPreference: normalizedPreference, profile }),
  }
}

function rankStrategies({ operatingPreference, profile }) {
  return INVESTMENT_STRATEGIES
    .map((strategy) => ({
      ...strategy,
      score: strategyScore({ strategy, operatingPreference, profile }),
    }))
    .sort((left, right) => right.score - left.score)
}

function strategyScore({ strategy, operatingPreference, profile }) {
  let score = styleScore(strategy, operatingPreference)
  score += cashflowScore(strategy, profile)
  score += capitalScore(strategy, profile)
  score += timeframeScore(strategy, profile)
  score += deliveryScore(strategy, profile)

  if (strategy.id === "flips" && profile.totalCapital >= 500000) score -= 12
  if (strategy.id === "commercial-conversion" && profile.totalCapital >= 500000) score += 10

  return score
}

function styleScore(strategy, operatingPreference) {
  if (strategy.stats.bestFitStyle === operatingPreference) return 40
  if (STYLE_ADJACENCY[operatingPreference]?.includes(strategy.stats.bestFitStyle)) return 18

  return 4
}

function cashflowScore(strategy, profile) {
  const score = CASHFLOW_SCORE[strategy.stats.cashflow] || 0
  if (profile.cashflow >= 10000 || profile.cashflowPerYear >= 4000) return score * 6
  if (profile.cashflow > 0) return score * 3

  return 0
}

function capitalScore(strategy, profile) {
  const capital = profile.totalCapital

  if (capital >= 500000) {
    if (strategy.id === "commercial-conversion") return 28
    if (strategy.id === "urban-goldmine") return 18
    if (strategy.id === "cowork-serviced-office") return 16
    if (strategy.id === "pure-commercial") return 12
    return 8
  }

  if (capital >= 100000) {
    if (["sa", "hmo", "urban-goldmine", "cowork-serviced-office"].includes(strategy.id)) return 16
    if (strategy.id === "commercial-conversion") return 10
    return 8
  }

  if (capital >= 50000) {
    if (["hmo", "urban-goldmine", "brrr"].includes(strategy.id)) return 14
    if (["sa", "cowork-serviced-office"].includes(strategy.id)) return 8
    return 4
  }

  if (["brrr", "flips"].includes(strategy.id)) return 10

  return 2
}

function timeframeScore(strategy, profile) {
  if (!profile.timeframe) return 0

  const speed = SPEED_SCORE[strategy.stats.speed] || 0
  if (profile.timeframe <= 2) {
    if (strategy.id === "commercial-conversion") return 12

    return speed * 4
  }

  if (profile.timeframe >= 7) return (4 - (COMPLEXITY_SCORE[strategy.stats.complexity] || 1)) * 4

  return speed * 3
}

function deliveryScore(strategy, profile) {
  if (!profile.executionScore) return 0

  const complexity = COMPLEXITY_SCORE[strategy.stats.complexity] || 0
  if (profile.executionScore >= 70) return complexity * 4
  if (profile.executionScore <= 35) return (4 - complexity) * 4

  return 0
}

function nextSecondaryStrategy({ requestedSecondary, primaryStrategy, rankedStrategies }) {
  if (validStrategyId(requestedSecondary) && requestedSecondary !== primaryStrategy) return requestedSecondary

  return rankedStrategies.find((strategy) => strategy.id !== primaryStrategy)?.id || null
}

function stretchStrategy({ primaryStrategy, secondaryStrategy, rankedStrategies, profile }) {
  const selected = new Set([primaryStrategy, secondaryStrategy].filter(Boolean))
  const capitalReady = profile.totalCapital >= 100000 || profile.timeframe >= 3
  const stretchFirst = capitalReady ? ["commercial-conversion", "urban-goldmine", "sa", "cowork-serviced-office"] : ["hmo", "urban-goldmine", "brrr"]
  const stretchId = stretchFirst.find((id) => !selected.has(id))
  if (stretchId) return stretchId

  return rankedStrategies.find((strategy) => !selected.has(strategy.id))?.id || null
}

function recommendationCards({ primaryId, secondaryId, stretchId, operatingPreference, profile }) {
  return [
    {
      role: "Primary Strategy",
      label: "Best fit",
      strategy: findStrategy(primaryId),
      rationale: rationaleFor({ role: "primary", strategyId: primaryId, operatingPreference, profile }),
    },
    {
      role: "Secondary Strategy",
      label: "Alternative fit",
      strategy: findStrategy(secondaryId),
      rationale: rationaleFor({ role: "secondary", strategyId: secondaryId, operatingPreference, profile }),
    },
    {
      role: "Stretch Strategy",
      label: "Higher upside / more complexity",
      strategy: findStrategy(stretchId),
      rationale: rationaleFor({ role: "stretch", strategyId: stretchId, operatingPreference, profile }),
    },
  ].filter((recommendation) => recommendation.strategy)
}

function rationaleFor({ role, strategyId, operatingPreference, profile }) {
  if (role === "stretch") {
    return "Higher upside route if you decide the extra complexity and delivery risk are worth taking on."
  }

  if (strategyId === "sa") return "Strong fit for operational involvement and stronger monthly cashflow goals."
  if (strategyId === "hmo") return "More active, resilient recurring income without becoming a full hospitality business."
  if (strategyId === "commercial-conversion") return "Uses capital depth and execution appetite for larger repositioning upside."
  if (strategyId === "pure-commercial" && operatingPreference === "passive" && passiveTension(profile)) {
    return "Low-involvement fit, but the target may need more time or more operational leverage."
  }
  if (strategyId === "pure-commercial") return "Lower-involvement route with steadier, slower income build-up."
  if (strategyId === "urban-goldmine") return "Useful middle path between active management, repositioning potential and cashflow."
  if (strategyId === "cowork-serviced-office") return "Operational commercial income route where business demand supports the asset."
  if (strategyId === "brrr") return "Active value-add route that can recycle capital into a longer portfolio build."

  return `Fits the ${operatingPreferenceLabel(operatingPreference)} involvement level and your Step One constraints.`
}

function systemInsightFor({ operatingPreference, profile }) {
  if (operatingPreference === "passive" && passiveTension(profile)) {
    return "I'll be honest - that target may be difficult using passive strategies alone. To hit that level of cashflow quickly, most investors typically become more operational or more execution-heavy."
  }

  if (operatingPreference === "heavy-execution" && profile.totalCapital >= 500000) {
    return "Your capital depth and execution appetite make larger repositioning projects more credible than quick, one-off flips."
  }

  if (profile.cashflow >= 10000 || profile.cashflowPerYear >= 4000) {
    return "Based on your targets, achieving this outcome will likely require stronger-than-average cashflow strategies."
  }

  if (profile.timeframe >= 7) {
    return "Your timeframe gives you enough flexibility to prioritise lower complexity and stronger predictability."
  }

  return "Based on your goals and preferred involvement, I would focus here first."
}

function coachingTensionsFor({ operatingPreference, profile }) {
  if (operatingPreference === "passive" && passiveTension(profile)) {
    return ["Increase timeframe", "Increase involvement"]
  }

  return []
}

function passiveTension(profile) {
  return profile.cashflow >= 10000 && profile.timeframe <= 3 && profile.totalCapital <= 200000
}

function operatingPreferenceLabel(value) {
  return OPERATING_STYLES.find((style) => style.id === value)?.title || "selected"
}

function validStrategyId(strategyId) {
  return STRATEGY_BY_ID.has(strategyId) ? strategyId : null
}

function stepOneProfile(stepOneValues) {
  const cashflow = numericValue(stepOneValues.targetMonthlyCashflowGross)
  const capital = numericValue(stepOneValues.availableCapital)
  const jvCapital = numericValue(stepOneValues.jvCapitalAccess)
  const timeframe = numericValue(stepOneValues.timeframeYears)
  const totalCapital = capital + jvCapital

  return {
    cashflow,
    capital,
    jvCapital,
    totalCapital,
    timeframe,
    cashflowPerYear: timeframe > 0 ? cashflow / timeframe : cashflow,
    executionScore: numericValue(stepOneValues.executionScore),
  }
}

function numericValue(value) {
  return Number.parseFloat(String(value || "").replace(/[^0-9.]/g, "")) || 0
}
